import { Tag } from 'antd';
import styled from 'styled-components';
import { colors, rgbaFromHex, insideIsDark } from '../../utils/colorSystem';

/** e.g. submitted_to_qa -> "Submitted", active -> "Active" */
const formatStatusLabel = (status) => {
    if (!status) return status;
    const s = String(status).trim();
    if (s.toLowerCase() === 'in_progress') return 'In Progress';
    if (s.toLowerCase() === 'submitted_to_qa' || s.toLowerCase() === 'submitted_to_pm') return 'Submitted';
    if (s.toLowerCase() === 'returned_by_qa') return 'Returned by QA';
    if (s.toLowerCase() === 'pending_completion') return 'Pending completion';
    if (s.includes('_')) {
        return s
            .split('_')
            .map((word) => (word.length > 0 ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : ''))
            .join(' ');
    }
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

export const StatusBadge = ({ status, children, size, ...props }) => {
    const label = children ?? formatStatusLabel(status);
    return (
        <StyledStatusBadge
            $status={status}
            $small={size === 'small'}
            $xs={size === 'xs'}
            $mini={size === 'mini'}
            $compact={size === 'compact'}
            {...props}
        >
            {label}
        </StyledStatusBadge>
    );
};

const getStatusCategory = (status) => {
    const s = String(status).toLowerCase().trim();
    if (
        s === 'active' ||
        s === 'fixed' ||
        s === 'resolved' ||
        s === 'accepted' ||
        s === 'paid' ||
        s === 'genuine' ||
        s === 'done' ||
        s === 'completed' ||
        s === 'delivered' ||
        s === 'success'
    )
        return 'success';

    if (
        s === 'inactive' ||
        s === 'deactivated' ||
        s === 'rejected' ||
        s === 'declined' ||
        s === 'closed' ||
        s === 'improper' ||
        s === 'issue' ||
        s === 'error' ||
        s === 'returned' ||
        s === 'returned_by_qa' ||
        s === 'returned by qa'
    )
        return 'error';

    if (
        s === 'suspended' ||
        s === 'pending' ||
        s === 'pending_completion' ||
        s === 'pending completion' ||
        s === 'in_review' ||
        s === 'in review' ||
        s === 'in_progress' ||
        s === 'in progress' ||
        s === 'warning'
    )
        return 'warning';

    return 'primary'; // Purple for everything else: submitted, acknowledged, invited, awarded, open, todo, upgrade, primary, etc.
};

const getStatusColors = (status, isDark) => {
    const s = String(status || '').toLowerCase().trim();
    if (s === 'late') {
        if (isDark) {
            return {
                bg: 'rgba(234, 179, 8, 0.15)',
                text: '#fde047',
                border: 'rgba(234, 179, 8, 0.3)',
            };
        }
        return {
            bg: '#fef9c3',
            text: '#ca8a04',
            border: '#fef08a',
        };
    }

    const category = getStatusCategory(status);
    const c = colors[category] || colors.primary;

    if (isDark) {
        return {
            bg: rgbaFromHex(c[500], 0.15),
            text: c[400] || c[500],
            border: rgbaFromHex(c[500], 0.25),
        };
    }

    return {
        bg: c[50],
        text: c[700] || c[600],
        border: c[100],
    };
};

// Removed redundant normalizeStatusForColors and old getStatusColors

const StyledStatusBadge = styled(Tag)`
    display: inline-flex;
    align-items: center;
    padding: ${({ $small, $xs, $mini, $compact }) =>
        $compact ? '1px 6px' : $mini ? '1px 6px' : $xs ? '4px 10px' : $small ? '2px 8px' : '4px 12px'};
    border-radius: ${({ $compact }) => ($compact ? '8px' : '9999px')};
    font-size: ${({ $small, $xs, $mini, $compact }) =>
        $compact ? '10px' : $mini ? '10px' : $xs ? '11px' : $small ? '11px' : '14px'};
    font-weight: 500;
    background: ${({ theme, $status }) => getStatusColors($status, insideIsDark(theme)).bg} !important;
    color: ${({ theme, $status }) => getStatusColors($status, insideIsDark(theme)).text} !important;
    border: 1px solid ${({ theme, $status }) => getStatusColors($status, insideIsDark(theme)).border} !important;
    font-family: var(--font-family-geist);
    margin: 0 !important;
    flex-shrink: 0;

    @media (max-width: 768px) {
        font-size: ${({ $small, $xs, $mini, $compact }) => ($compact || $mini ? '9px' : $xs || $small ? '10px' : '13px')};
        padding: ${({ $small, $xs, $mini, $compact }) =>
        $compact ? '1px 5px' : $mini ? '0 5px' : $xs ? '3px 8px' : $small ? '2px 6px' : '3px 10px'};
    }

    @media (max-width: 480px) {
        font-size: ${({ $small, $xs, $mini, $compact }) => ($compact || $mini ? '9px' : $xs || $small ? '10px' : '12px')};
        padding: ${({ $small, $xs, $mini, $compact }) =>
        $compact ? '1px 4px' : $mini ? '0 4px' : $xs ? '2px 6px' : $small ? '1px 5px' : '2px 8px'};
    }
`;

export default StatusBadge;
